import {
    applyRemoveProjectInvite,
    applyRemoveRole,
    applySetDocumentAsPrivate,
    applySetEnterpriseMemberPermissionType,
    applySetOrganizationMemberPermissionType,
    applySetProjectInvite,
    applySetRole,
} from "../actions/permissionActions";
import {performDocumentMutation} from "../documentMutations";
import type {DocumentStoreState} from "../types";

import type {SliceDependencies} from "./types";

type PermissionSliceState = Pick<
    DocumentStoreState,
    | "setRole"
    | "removeRole"
    | "setProjectInvite"
    | "removeProjectInvite"
    | "setDocumentAsPrivate"
    | "setEnterpriseMemberPermissionType"
    | "setOrganizationMemberPermissionType"
>;

export function createPermissionSlice(deps: SliceDependencies): PermissionSliceState {
    const {primitives, get} = deps;

    return {
        setRole: (role) => {
            const document = get().document;
            if (!document?.roles) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetRole(draft, role), {
                actionType: "setRole",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        removeRole: (roleUid) => {
            const document = get().document;
            if (!document?.roles || !roleUid) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRemoveRole(draft, roleUid), {
                actionType: "removeRole",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        setProjectInvite: (invite) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetProjectInvite(draft, invite), {
                actionType: "setProjectInvite",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        removeProjectInvite: (invite) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applyRemoveProjectInvite(draft, invite), {
                actionType: "removeProjectInvite",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        setDocumentAsPrivate: () => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(primitives, (draft) => applySetDocumentAsPrivate(draft), {
                actionType: "setDocumentAsPrivate",
                canUndo: false,
                shouldGenerateActionRecord: true,
                updatesDocumentTimestamp: false,
            });
        },

        setEnterpriseMemberPermissionType: (permissionType) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applySetEnterpriseMemberPermissionType(draft, permissionType),
                {
                    actionType: "setEnterpriseMemberPermissionType",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: false,
                },
            );
        },

        setOrganizationMemberPermissionType: (permissionType) => {
            const document = get().document;
            if (!document) {
                return;
            }
            performDocumentMutation(
                primitives,
                (draft) => applySetOrganizationMemberPermissionType(draft, permissionType),
                {
                    actionType: "setOrganizationMemberPermissionType",
                    canUndo: false,
                    shouldGenerateActionRecord: true,
                    updatesDocumentTimestamp: false,
                },
            );
        },
    };
}
